// Conversión API -> modelo local + merge por clave lógica
import type { ProductoCarrito } from "./cart-types";
import { toNum, r2 } from "./cart-utils";
import { esTransferGenerico, extraerLaboratorioGenerico, unificarProductosTransfer } from "../hybrid/transfer-helpers";

export const logicalKey = (p: ProductoCarrito): string => {
  if ((p.es_combo || p.combo_id) && p.combo_id) return `COMBO:${String(p.combo_id)}`;
  const lab = esTransferGenerico(p) ? extraerLaboratorioGenerico(p) : "";

  // 🔑 Incluir tipo_solicitante y observacion para diferenciar revista de oferta
  // Esto permite que el mismo producto en revista vs en oferta sean líneas separadas
  const tipoSolic = (p as any).tipo_solicitante || "C";
  const obs = String((p as any).observacion || "").toLowerCase();
  const esRevista = obs.includes("revista");

  // Agregar revista o tipo_solicitante a la clave lógica
  const suffix = esRevista ? ":REVISTA" : (tipoSolic !== "C" ? `:${tipoSolic}` : "");

  return lab ? `TRF:${p.cod_drog}:${lab}${suffix}` : `SKU:${p.cod_drog}${suffix}`;
};

export function mergeByLogicalKeySum(items: ProductoCarrito[]): ProductoCarrito[] {
  const map = new Map<string, ProductoCarrito>();
  for (const it of items) {
    const k = logicalKey(it);
    if (!map.has(k)) { map.set(k, { ...it }); continue; }
    const acc = map.get(k)!;
    const qNew = Math.max(1, Number(it.cantidad) || 1);
    const qAcc = Math.max(1, Number(acc.cantidad) || 1);
    const newQty = qAcc + qNew;
    const newTotal = (acc.precio_total || 0) + (it.precio_total || 0);
    const newUnit = newQty > 0 ? newTotal / newQty : acc.precio_unitario;
    const merged = { ...acc, cantidad: newQty, precio_total: r2(newTotal), precio_unitario: r2(newUnit), id: acc.id ?? it.id };

    // ✅ PRESERVAR campos extras (unidades_por_modulo, etc.) en el merge
    const accAny = acc as any;
    const itAny = it as any;
    const mergedAny = merged as any;
    if (itAny.unidades_por_modulo != null) mergedAny.unidades_por_modulo = itAny.unidades_por_modulo;
    if (accAny.unidades_por_modulo != null && mergedAny.unidades_por_modulo == null) mergedAny.unidades_por_modulo = accAny.unidades_por_modulo;

    map.set(k, merged);
  }
  return Array.from(map.values());
}

// ===== convertirDeAPI (idéntica lógica, compactada) =====
export function convertirDeAPI(api: any): ProductoCarrito {
  const cantidad = Math.max(1, toNum(api.cantidad, 1));
  const esCombo = Boolean(api.es_combo);
  const precioUnitApi = toNum(api.precio_unitario, 0);
  const precioCampo = toNum(api.precio, 0);
  const precioOferta = toNum(api.precio_oferta, 0);
  const precioSi = toNum(api.precio_si, 0);
  const prFarmaciaSi = toNum(api.pr_farmacia_si, 0);
  const prFarmacia = toNum(api.pr_farmacia, 0);
  const descPorc = toNum(api.desc_porc ?? api.descuento, 0);
  const tipoSolic = api.tipo_solicitante || "C";

  let precio_unitario_final = 0;
  let precio_total_final = 0;

  if (esCombo && api.combo_id) {
    // Para combos: monto_total es el total de la línea completa (todas las unidades)
    // precio_unitario es monto_total / cantidad (precio por unidad del combo)
    // Usamos monto_total directamente como precio_total (es el valor correcto de la línea)
    const mt = toNum(api.monto_total, 0);

    let unitSinIVA = 0;
    if (precioSi > 0) {
      unitSinIVA = precioSi;
    } else if (mt > 0 && cantidad > 0) {
      // monto_total ya es el total sin IVA (o con IVA según backend) — lo usamos directo
      // precio_unitario del API = monto_total / cantidad, lo verificamos
      unitSinIVA = r2(mt / cantidad);
    } else if (precioUnitApi > 0) {
      unitSinIVA = precioUnitApi;
    }

    precio_unitario_final = r2(unitSinIVA);
    precio_total_final = mt > 0 ? r2(mt) : r2(precio_unitario_final * cantidad);
  } else {
    // ✅ CORREGIDO: Usar PRIORIDADES en lugar de Math.min (que tomaba el menor incorrectamente)
    // Orden de prioridad: Oferta > Descuento > SinIVA > UnitarioAPI > Precio general
    let vigente = 0;

    // Si es oferta o tiene descuento, preferir precios de oferta
    if (tipoSolic === "O" || descPorc > 0) {
      vigente = precioOferta > 0 ? precioOferta : precioSi > 0 ? precioSi : prFarmaciaSi > 0 ? prFarmaciaSi : 0;
    }

    // Si no encontramos precio de oferta, usar otros
    if (vigente <= 0) {
      vigente = prFarmaciaSi > 0 ? prFarmaciaSi : precioUnitApi > 0 ? precioUnitApi : precioCampo > 0 ? precioCampo : precioSi > 0 ? precioSi : 0;
    }

    precio_unitario_final = r2(vigente);
    precio_total_final = r2(vigente * cantidad);
  }

  // precio de lista (tachado)
  let listaUnit = 0;
  if (!esCombo) {
    const v = precio_unitario_final; const EPS = 0.005;
    if (prFarmacia > 0 && prFarmacia >= v + EPS) listaUnit = prFarmacia;
    else if (precioCampo > 0 && precioCampo >= v + EPS) listaUnit = precioCampo;
    else if (v > 0 && descPorc > 0 && descPorc < 100) {
      const rec = v / (1 - descPorc / 100);
      if (rec > v + EPS) listaUnit = rec;
    } else {
      const prPublico = toNum(api.pr_publico, 0);
      listaUnit = prPublico > 0 && prPublico >= v + EPS ? prPublico : v;
    }
  }
  listaUnit = r2(listaUnit);

  const p: ProductoCarrito = {
    id: api.id_pedido || api.id || `api_${Date.now()}_${Math.random()}`,
    cod_drog: api.cod_drog || "",
    descripcion: api.descripcion || "Sin descripción",
    precio_unitario: r2(precio_unitario_final),
    cantidad,
    precio_total: r2(precio_total_final),

    precio_lista_unitario: listaUnit,
    precio_lista_total: r2(listaUnit * cantidad),

    imagen: api.imagen,
    descuento: toNum(api.descuento, 0),
    tipo_solicitante: api.tipo_solicitante || "C",
    es_combo: esCombo,
    combo_id: api.combo_id,
    combo: api.combo ?? esCombo,
    observacion: api.observacion,
    rubro: api.rubro,
    laboratorio: api.laboratorio,
    marca: api.marca || api.titulo,
    expendedor: api.expendedor,
    oferta: api.oferta,
    refrigerado: api.refrigerado,
    transfer: api.transfer,
    fora: api.fora,
    dto_general: api.dto_general,
    iva: api.iva,
    stock: api.stock,
    unidxbulto: api.unidxbulto,
    idimagen: api.idimagen,
    cod_barra: api.cod_barra,
    es_transfer: esTransferGenerico(api),
  };

  // ✅ CORREGIDO: Preservar TODOS los campos que vienen del API original
  // ya que cart-api-service.ts los mapea correctamente
  // 🔴 CRÍTICO: Estos campos son esenciales para cart-page.ts
  (p as any).precio = toNum(api.precio, 0); // CON descuento (para "Precio c/Dto s/IVA")
  (p as any).precio_unitario_sin_iva = toNum(api.precio_unitario_sin_iva, 0); // SIN descuento (para "Precio s/IVA")
  (p as any).importe_sin_iva = toNum(api.importe_sin_iva, 0); // Total SIN IVA (para "Importe")
  (p as any).importe_con_iva = toNum(api.importe_con_iva, 0); // Total CON IVA

  // ✅ PRESERVAR descuentos tal como vienen del API
  // La limpieza de descuentos ocurre SOLO en cart-api-service.agregarProducto()
  // cuando el usuario elige "Mantener" en ese momento (tipo_solicitante="C" && cumple_oferta=false)
  // Durante la sincronización, confiamos en lo que el servidor devuelve

  if (precioSi > 0) (p as any).precio_si = r2(precioSi);
  if (prFarmaciaSi > 0) (p as any).pr_farmacia_si = r2(prFarmaciaSi);
  if (precioOferta > 0) (p as any).precio_oferta = r2(precioOferta);

  if (descPorc > 0) (p as any).desc_porc = r2(descPorc);

  if (prFarmacia > 0) (p as any).pr_farmacia = r2(prFarmacia);
  if (esCombo && api.monto_total) (p as any).monto_total = r2(toNum(api.monto_total));

  if (api.cant_revista != null) (p as any).cant_revista = Number(api.cant_revista);

  // ✅ PRESERVAR unidades_por_modulo para validación de cantidad en carrito
  if (api.unidades_por_modulo != null) {
    (p as any).unidades_por_modulo = Number(api.unidades_por_modulo);
  }

  // ✅ PRESERVAR cantmin_venta para validación de cantidad mínima en carrito
  if (api.cantmin_venta != null) {
    (p as any).cantmin_venta = Number(api.cantmin_venta);
  }

  // ✅ PRESERVAR auditoría de carga (quién cargó el producto)
  if (api.usuario_carga) (p as any).usuario_carga = String(api.usuario_carga);
  if (api.nombre_carga) (p as any).nombre_carga = String(api.nombre_carga);

  return p;
}

export const finalizeTransfers = (arr: ProductoCarrito[]) => unificarProductosTransfer(arr);
